import type {
  BrowsePage,
  ConnectorConnection,
  ConnectorFolder,
  ConnectorProvider,
  ConnectorRoot,
  ConnectorSurface,
  KnowledgeBase
} from '@ralph/contracts'
import { PROVIDER_LABEL, SURFACE_LABEL, SURFACE_PROVIDER } from '@ralph/contracts'
import clsx from 'clsx'
import { ChevronRight, KeyRound, TriangleAlert } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { Button, ChoiceRow, Input, Panel, Spinner, Textarea } from '@/components/ui/primitives.tsx'
import {
  useBrowse,
  useConnections,
  useConnectorAvailability,
  useCreateConnection,
  useCreateFolder,
  useS3Policy,
  useStartFolder,
  useStartSignIn,
  useViewer
} from '@/hooks/use-connectors.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'
import { mayUseConnection } from './connected-folders.ts'
import { FolderPreview } from './FolderPreview.tsx'
import { ProviderMark } from './ProviderMark.tsx'
import type { PendingRule } from './SelectionTable.tsx'

/**
 * Connect a folder (PRD-25): three panels, disclosed progressively, in the pane.
 *
 *   Account → Folder → What will be loaded
 *
 * No wizard, no modal, no Next buttons. Each panel appears below the last, in document
 * order, so tab order is reading order for free. The account step is the consequential half
 * of authentication and the only half worth showing: whose eyes the platform borrows, said as
 * a consequence beside each choice rather than as "delegated" versus "application". A sign-in
 * is a real top-level navigation to the provider; the flow survives it because the return
 * lands on the base's folders tab with `?connected=<connectionId>` and resumes at the folder
 * step.
 *
 * Accounts are offered as the server allows them (PRD-26 D-38): a person reads through their
 * own sign-ins; company accounts — S3 keys, a service account, the organisation's app — are
 * added and used by administrators only, so nobody else is shown a form that would be refused.
 */

type Step = 'account' | 'folder' | 'preview'

export const ConnectFolderFlow = ({
  base,
  resumeConnectionId,
  resumeFolder,
  onDone,
  onCancel
}: {
  base: Pick<KnowledgeBase, 'id' | 'name' | 'access' | 'owner' | 'lentToCount'>
  /** After a sign-in redirect: the account that was just connected. */
  resumeConnectionId?: string | null
  /** A preview left earlier and reopened from the list. */
  resumeFolder?: ConnectorFolder | null
  onDone: (folderId: string) => void
  onCancel: () => void
}) => {
  // In the body, never at module scope — a capture at import freezes the language (PRD-27 F-3).
  const messages = useI18n().t
  const t = messages.knowledgeFolders
  const availability = useConnectorAvailability()
  const connections = useConnections()
  const [step, setStep] = useState<Step>(resumeFolder ? 'preview' : resumeConnectionId ? 'folder' : 'account')
  const [connection, setConnection] = useState<ConnectorConnection | null>(null)
  const [surface, setSurface] = useState<ConnectorSurface | null>(resumeFolder?.surface ?? null)
  const [folder, setFolder] = useState<ConnectorFolder | null>(resumeFolder ?? null)
  const [pendingRules, setPendingRules] = useState<Map<string, PendingRule>>(new Map())
  const create = useCreateFolder()
  const start = useStartFolder()

  // Resuming after a sign-in: the connection arrives by id once the list has loaded.
  useEffect(() => {
    if (!resumeConnectionId || connection || !connections.data) return
    const found = connections.data.items.find((c) => c.id === resumeConnectionId)
    if (found) {
      setConnection(found)
      setSurface(found.provider === 'microsoft' ? 'sharepoint' : found.provider === 'google' ? 'drive' : 's3')
      setStep('folder')
    }
  }, [resumeConnectionId, connection, connections.data])

  const choose = (root: ConnectorRoot, displayPath: string) => {
    if (!connection) return
    // `mutate` with callbacks rather than an awaited `mutateAsync`: a refused create renders from
    // the mutation's own state below, instead of escaping as an unhandled rejection.
    create.mutate(
      { sourceId: base.id, connectionId: connection.id, root, displayPath },
      {
        onSuccess: (created) => {
          setFolder(created)
          setStep('preview')
        }
      }
    )
  }

  return (
    <Panel solid className="min-w-0 flex-1 px-4 py-4 sm:px-5">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-[16px]">{t.connect}</h2>
          <p className="mt-1 max-w-lg text-pretty text-[13px] leading-[1.5] text-fg-muted">{t.emptyBody(base.name)}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t.preview.cancel}
        </Button>
      </div>

      <div className="mt-4 space-y-4">
        <AccountPanel
          baseId={base.id}
          availability={availability.data ?? null}
          connections={connections.data?.items ?? []}
          chosen={connection}
          surface={surface}
          collapsed={step !== 'account'}
          onChoose={(picked, pickedSurface) => {
            setConnection(picked)
            setSurface(pickedSurface)
            setStep('folder')
          }}
          onReopen={() => setStep('account')}
        />

        {step !== 'account' && connection && surface && step !== 'preview' && (
          <BrowsePanel connection={connection} surface={surface} onChoose={choose} choosing={create.isPending} />
        )}
        {create.isError && (
          <p role="alert" className="text-pretty text-[12.5px] leading-[1.45] text-alert">
            {describeApiError(messages, create.error)}
          </p>
        )}

        {step === 'preview' && folder && (
          <section aria-labelledby="preview-title">
            <div className="mb-3 flex flex-wrap items-baseline gap-x-2">
              <h3 id="preview-title" className="font-sans text-[14px] font-semibold tracking-[-0.018em]">
                {t.preview.title}
              </h3>
              <span className="readout text-fg-subtle">
                <ProviderMark surface={folder.surface} size={11} className="mr-1 inline align-[-1px]" />
                {folder.displayPath}
              </span>
            </div>
            <FolderPreview
              folder={folder}
              base={base}
              mode="connect"
              pendingRules={pendingRules}
              setPendingRules={setPendingRules}
              onCancel={onCancel}
              onStarted={(ack) =>
                start.mutate(
                  { id: folder.id, ack: ack.ack ? true : undefined, ackText: ack.ackText, ackLocale: ack.ackLocale },
                  { onSuccess: (started) => onDone(started.id) }
                )
              }
            />
            {start.isError && (
              <p role="alert" className="mt-2 text-pretty text-[12.5px] leading-[1.45] text-alert">
                {describeApiError(messages, start.error)}
              </p>
            )}
          </section>
        )}
      </div>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------

const AccountPanel = ({
  baseId,
  availability,
  connections,
  chosen,
  surface,
  collapsed,
  onChoose,
  onReopen
}: {
  baseId: string
  availability: {
    microsoft: { configured: boolean; tenantId: string }
    google: { configured: boolean }
    redirectUri: string
  } | null
  connections: ConnectorConnection[]
  chosen: ConnectorConnection | null
  surface: ConnectorSurface | null
  collapsed: boolean
  onChoose: (connection: ConnectorConnection, surface: ConnectorSurface) => void
  onReopen: () => void
}) => {
  const [provider, setProvider] = useState<ConnectorProvider | null>(chosen?.provider ?? null)
  const [pickedSurface, setPickedSurface] = useState<ConnectorSurface | null>(surface)
  const signIn = useStartSignIn()
  const viewer = useViewer()
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders

  if (collapsed && chosen && surface) {
    return (
      <Panel className="flex flex-wrap items-center gap-3 px-4 py-3">
        <ProviderMark surface={surface} size={14} className="text-fg-muted" />
        <span className="min-w-0 flex-1 text-[13px]">
          <span className="font-medium">{chosen.accountLabel}</span>
          <span className="readout ml-2 text-fg-subtle">{SURFACE_LABEL[surface]}</span>
        </span>
        <Button size="sm" variant="ghost" onClick={onReopen}>
          {t.accounts.change}
        </Button>
      </Panel>
    )
  }

  // The hint is read from the catalogue's per-surface map rather than written here, so a fifth
  // surface is a compile error in both languages instead of a glyph with nothing under it.
  const surfaces: Array<{ value: ConnectorSurface; available: boolean }> = [
    { value: 'sharepoint', available: availability?.microsoft.configured ?? false },
    { value: 'onedrive', available: availability?.microsoft.configured ?? false },
    { value: 'drive', available: availability?.google.configured ?? false },
    // A bucket is read with keys — a company account — so only an administrator can connect one.
    { value: 's3', available: viewer.admin }
  ]
  const missing = surfaces.filter((s) => !s.available && s.value !== 's3').map((s) => SURFACE_LABEL[s.value])
  // A person's own sign-in reads as them, so only they (or an administrator) may read through
  // it; company accounts read far more than any one person may see, so only administrators may
  // use them. The server lists no other account, and this filter says so rather than trusting it.
  const forProvider = pickedSurface
    ? connections.filter((c) => c.provider === SURFACE_PROVIDER[pickedSurface] && mayUseConnection(c, viewer))
    : []

  return (
    <Panel className="px-4 py-3.5">
      <span className="eyebrow mb-2 block text-fg-muted">{t.accounts.whereFiles}</span>
      <div className="flex flex-wrap gap-2">
        {surfaces
          .filter((s) => s.available)
          .map((s) => (
            <button
              key={s.value}
              type="button"
              aria-pressed={pickedSurface === s.value}
              onClick={() => {
                setPickedSurface(s.value)
                setProvider(SURFACE_PROVIDER[s.value])
              }}
              className={clsx(
                'flex items-center gap-2.5 rounded-tile border px-3 py-2 text-left transition-[background-color,border-color,box-shadow] duration-200',
                pickedSurface === s.value
                  ? 'border-rule-strong bg-card-solid shadow-glass'
                  : 'border-rule bg-card-solid/55 hover:border-rule-strong'
              )}
            >
              <span className="flex h-[30px] w-[30px] items-center justify-center rounded-tile border border-rule bg-card-solid">
                <ProviderMark surface={s.value} size={15} className="text-fg-muted" />
              </span>
              <span>
                <span className="block text-[13px] font-semibold tracking-[-0.014em]">{SURFACE_LABEL[s.value]}</span>
                <span className="block text-[12px] text-fg-muted">{t.accounts.surfaceHint[s.value]}</span>
              </span>
            </button>
          ))}
      </div>
      {missing.length > 0 && (
        <p className="mt-2 text-[12px] leading-[1.45] text-fg-subtle">{t.accounts.notConfigured(fmt, missing)}</p>
      )}
      {!viewer.admin && <p className="mt-1 text-[12px] leading-[1.45] text-fg-subtle">{t.accounts.s3AdminOnly}</p>}

      {pickedSurface && provider && (
        <div className="mt-4">
          <span className="eyebrow mb-1.5 block text-fg-muted">{t.accounts.chooseAccount}</span>
          {forProvider.length > 0 && (
            <ChoiceRow
              name="connector-account"
              value={chosen?.id ?? ''}
              options={forProvider.map((c) => ({
                value: c.id,
                label: t.accounts.optionLabel(c.accountLabel, PROVIDER_LABEL[c.provider], c.mode === 'application'),
                hint:
                  c.mode === 'delegated'
                    ? `${t.accounts.signedIn(fmt.formatAgo(c.createdAt))}. ${t.accounts.usedBy(fmt, c.folderCount)}. ${t.accounts.delegatedStops(c.ownerName)}`
                    : `${t.accounts.applicationHint} ${t.accounts.usedBy(fmt, c.folderCount)}.`
              }))}
              onChange={(id) => {
                const picked = forProvider.find((c) => c.id === id)
                if (picked) onChoose(picked, pickedSurface)
              }}
            />
          )}

          <div className={clsx('space-y-3', forProvider.length > 0 && 'mt-3 border-t border-rule pt-3')}>
            {provider !== 's3' && (
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant={forProvider.length === 0 ? 'primary' : 'secondary'}
                  icon={<KeyRound size={13} aria-hidden />}
                  disabled={signIn.isPending}
                  onClick={() =>
                    signIn.mutate(
                      { provider, sourceId: baseId },
                      // A full navigation, never a fetch: the provider renders its own consent
                      // page in its own origin with its own address bar.
                      { onSuccess: (started) => window.location.assign(started.authorizeUrl) }
                    )
                  }
                >
                  {forProvider.length === 0 ? t.accounts.signInWith(provider) : t.accounts.differentAccount(provider)}
                </Button>
                <span className="text-[12.5px] leading-[1.45] text-fg-muted">{t.accounts.opensSignIn(provider)}</span>
              </div>
            )}
            <p className="text-pretty text-[12.5px] leading-[1.45] text-fg-muted">
              {t.accounts.delegatedHint(SURFACE_LABEL[pickedSurface])}
            </p>
            {signIn.isError && (
              <p role="alert" className="text-[12.5px] text-alert">
                {describeApiError(fmt.t, signIn.error)}
              </p>
            )}
            {viewer.admin ? (
              <KeylessForm
                provider={provider}
                tenantId={availability?.microsoft.tenantId ?? ''}
                onCreated={(c) => onChoose(c, pickedSurface)}
              />
            ) : (
              <p className="text-pretty text-[12px] leading-[1.45] text-fg-subtle">{t.accounts.companyAdminOnly}</p>
            )}
          </div>
        </div>
      )}
    </Panel>
  )
}

/** S3 keys, a Google service account key, or a Microsoft application tenant: the accounts that need no browser. */
const KeylessForm = ({
  provider,
  tenantId,
  onCreated
}: {
  provider: ConnectorProvider
  tenantId: string
  onCreated: (connection: ConnectorConnection) => void
}) => {
  const messages = useI18n().t
  const t = messages.knowledgeFolders
  const create = useCreateConnection()
  const id = useId()
  const [open, setOpen] = useState(provider === 's3')
  const [s3, setS3] = useState({
    accessKeyId: '',
    secretAccessKey: '',
    sessionToken: '',
    region: 'eu-west-1',
    bucket: '',
    endpoint: '',
    forcePathStyle: false
  })
  const [serviceAccountKey, setServiceAccountKey] = useState('')
  const [tenant, setTenant] = useState(tenantId === 'organizations' ? '' : tenantId)
  const policy = useS3Policy(s3.bucket, '', provider === 's3' && s3.bucket.length >= 3)
  const [copied, setCopied] = useState(false)

  if (!open) {
    return (
      <Button size="sm" variant="ghost" aria-expanded={false} onClick={() => setOpen(true)}>
        {provider === 'google' ? t.accounts.useServiceAccount : t.accounts.useApplicationAccount}
      </Button>
    )
  }

  const submit = () => {
    if (provider === 's3') {
      create.mutate(
        {
          provider: 's3',
          accessKeyId: s3.accessKeyId.trim(),
          secretAccessKey: s3.secretAccessKey.trim(),
          ...(s3.sessionToken.trim() ? { sessionToken: s3.sessionToken.trim() } : {}),
          region: s3.region.trim(),
          bucket: s3.bucket.trim(),
          ...(s3.endpoint.trim() ? { endpoint: s3.endpoint.trim(), forcePathStyle: s3.forcePathStyle } : {})
        },
        { onSuccess: onCreated }
      )
    } else if (provider === 'google') {
      create.mutate({ provider: 'google', serviceAccountKey }, { onSuccess: onCreated })
    } else {
      create.mutate({ provider: 'microsoft', tenantId: tenant.trim() }, { onSuccess: onCreated })
    }
  }

  return (
    <form
      className="rounded-tile border border-rule bg-card-solid/55 px-3.5 py-3"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      {provider === 's3' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field id={`${id}-key`} label={t.accounts.s3.keyId}>
            <Input
              id={`${id}-key`}
              value={s3.accessKeyId}
              required
              autoComplete="off"
              onChange={(e) => setS3({ ...s3, accessKeyId: e.target.value })}
            />
          </Field>
          <Field id={`${id}-secret`} label={t.accounts.s3.secret}>
            <Input
              id={`${id}-secret`}
              type="password"
              value={s3.secretAccessKey}
              required
              autoComplete="off"
              onChange={(e) => setS3({ ...s3, secretAccessKey: e.target.value })}
            />
          </Field>
          <Field id={`${id}-bucket`} label={t.accounts.s3.bucket}>
            <Input
              id={`${id}-bucket`}
              value={s3.bucket}
              required
              onChange={(e) => setS3({ ...s3, bucket: e.target.value })}
            />
          </Field>
          <Field id={`${id}-region`} label={t.accounts.s3.region}>
            <Input
              id={`${id}-region`}
              value={s3.region}
              required
              onChange={(e) => setS3({ ...s3, region: e.target.value })}
            />
          </Field>
          <Field id={`${id}-token`} label={t.accounts.s3.sessionToken}>
            <Input
              type="password"
              id={`${id}-token`}
              value={s3.sessionToken}
              autoComplete="off"
              onChange={(e) => setS3({ ...s3, sessionToken: e.target.value })}
            />
          </Field>
          <Field id={`${id}-endpoint`} label={t.accounts.s3.endpoint}>
            <Input
              id={`${id}-endpoint`}
              value={s3.endpoint}
              placeholder="https://"
              onChange={(e) => setS3({ ...s3, endpoint: e.target.value })}
            />
          </Field>
          {s3.endpoint.trim() !== '' && (
            <label className="flex items-center gap-2 text-[12.5px] sm:col-span-2">
              <input
                type="checkbox"
                checked={s3.forcePathStyle}
                onChange={(e) => setS3({ ...s3, forcePathStyle: e.target.checked })}
              />
              {t.accounts.s3.pathStyle}
            </label>
          )}
          {policy.data && (
            <div className="sm:col-span-2">
              <span className="eyebrow block text-fg-muted">{t.accounts.s3.policyTitle}</span>
              <p className="mt-1 text-[12px] leading-[1.45] text-fg-muted">{t.accounts.s3.policyHint}</p>
              <pre className="mt-1.5 max-h-40 overflow-auto rounded-tile border border-rule bg-fg/[0.03] p-2 font-mono text-[10.5px] leading-[1.4]">
                {policy.data.policy}
              </pre>
              <Button
                size="sm"
                variant="mono"
                className="mt-1.5"
                onClick={() => {
                  void navigator.clipboard?.writeText(policy.data.policy).then(() => setCopied(true))
                }}
              >
                {copied ? t.accounts.s3.copied : t.accounts.s3.copy}
              </Button>
            </div>
          )}
        </div>
      )}
      {provider === 'google' && (
        <Field id={`${id}-sa`} label={t.accounts.serviceAccountLabel} hint={t.accounts.serviceAccountHint}>
          <Textarea
            id={`${id}-sa`}
            rows={5}
            value={serviceAccountKey}
            required
            onChange={(e) => setServiceAccountKey(e.target.value)}
            className="font-mono text-[11px]"
          />
        </Field>
      )}
      {provider === 'microsoft' && (
        <Field id={`${id}-tenant`} label={t.accounts.tenantLabel} hint={t.accounts.tenantHint}>
          <Input id={`${id}-tenant`} value={tenant} required onChange={(e) => setTenant(e.target.value)} />
        </Field>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" variant="primary" disabled={create.isPending}>
          {create.isPending ? t.accounts.checking : provider === 's3' ? t.accounts.useKeys : t.accounts.connectAccount}
        </Button>
        {create.isError && (
          <span role="alert" className="flex items-start gap-1.5 text-[12.5px] leading-[1.45] text-alert">
            <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
            {describeApiError(messages, create.error)}
          </span>
        )}
      </div>
    </form>
  )
}

const Field = ({
  id,
  label,
  hint,
  children
}: {
  id: string
  label: string
  hint?: string
  children: React.ReactNode
}) => (
  <div>
    <label htmlFor={id} className="eyebrow mb-1 block text-fg-muted">
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-[12px] leading-[1.45] text-fg-subtle">{hint}</p>}
  </div>
)

// ---------------------------------------------------------------------------
// Folder
// ---------------------------------------------------------------------------

/**
 * A breadcrumb and one list, one level at a time. Each row has two named affordances: the
 * name descends, `Choose` picks. For SharePoint the first level is a site search; for a
 * OneDrive read by an application account, the person whose files to open.
 */
const BrowsePanel = ({
  connection,
  surface,
  onChoose,
  choosing
}: {
  connection: ConnectorConnection
  surface: ConnectorSurface
  onChoose: (root: ConnectorRoot, displayPath: string) => void
  choosing: boolean
}) => {
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  const browse = useBrowse()
  const [at, setAt] = useState<Record<string, string>>({})
  const [query, setQuery] = useState('')
  const [user, setUser] = useState('')
  const [page, setPage] = useState<BrowsePage | null>(null)
  const [trail, setTrail] = useState<Array<{ at: Record<string, string>; name: string }>>([])

  const load = (next: Record<string, string>, extra: { query?: string; nextPage?: string } = {}) =>
    browse.mutate(
      {
        connectionId: connection.id,
        surface,
        at: next,
        ...(extra.query !== undefined ? { query: extra.query } : {}),
        ...(extra.nextPage ? { next: extra.nextPage } : {})
      },
      {
        onSuccess: (result) =>
          setPage(extra.nextPage && page ? { ...result, entries: [...page.entries, ...result.entries] } : result)
      }
    )

  // biome-ignore lint/correctness/useExhaustiveDependencies: the first level loads once per account and surface
  useEffect(() => {
    setAt({})
    setTrail([])
    setPage(null)
    if (surface === 'onedrive' && connection.mode === 'application') return
    load({})
  }, [connection.id, surface])

  const descend = (entry: BrowsePage['entries'][number]) => {
    let next: Record<string, string> = { ...at }
    if (surface === 'sharepoint') {
      if (!at.siteId) next = { siteId: entry.remoteId, siteName: entry.name }
      else if (!at.driveId) next = { ...at, driveId: entry.remoteId, driveName: entry.name }
      else
        next = {
          ...at,
          itemId: entry.remoteId,
          crumbs: JSON.stringify([...parseCrumbs(at.crumbs), { remoteId: entry.remoteId, name: entry.name }])
        }
    } else if (surface === 'onedrive') {
      next = {
        ...at,
        itemId: entry.remoteId,
        crumbs: JSON.stringify([...parseCrumbs(at.crumbs), { remoteId: entry.remoteId, name: entry.name }])
      }
    } else if (surface === 'drive') {
      const driveId =
        entry.kind === 'drive' && entry.remoteId !== 'root' && entry.remoteId !== 'shared-with-me'
          ? entry.remoteId
          : (at.driveId ?? '')
      next = {
        folderId: entry.remoteId,
        driveId,
        driveName: entry.kind === 'drive' ? entry.name : (at.driveName ?? ''),
        crumbs: JSON.stringify([...parseCrumbs(at.crumbs), { remoteId: entry.remoteId, name: entry.name }])
      }
    } else {
      next = { ...at, prefix: entry.remoteId === '/' ? '' : entry.remoteId }
    }
    setTrail([...trail, { at, name: entry.name }])
    setAt(next)
    load(next)
  }

  const goBack = (index: number) => {
    const target = trail[index]
    if (!target) return
    setTrail(trail.slice(0, index))
    setAt(target.at)
    load(target.at)
  }

  const chooseHere = () => {
    if (!page?.root) return
    // Every level entered, in order: the account, then each name the maintainer descended into.
    const path = [connection.accountLabel, ...trail.map((t2) => t2.name)].join(' / ')
    onChoose(page.root, surface === 's3' ? '' : path)
  }

  return (
    <Panel className="px-4 py-3.5">
      <span className="eyebrow mb-2 block text-fg-muted">{t.browse.title}</span>

      {surface === 'sharepoint' && !at.siteId && (
        <div className="mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[200px] flex-1">
              <Input
                value={query}
                placeholder={t.browse.findSite}
                aria-label={t.browse.findSite}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load({}, { query })}
              />
            </div>
            <Button size="sm" onClick={() => load({}, { query })}>
              {t.browse.search}
            </Button>
          </div>
          <p className="mt-1.5 text-[12px] leading-[1.45] text-fg-subtle">{t.browse.siteHint}</p>
        </div>
      )}
      {surface === 'onedrive' && connection.mode === 'application' && !at.driveId && (
        <div className="mb-3">
          <span className="eyebrow mb-1 block text-fg-muted">{t.browse.onedriveUser}</span>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[200px] flex-1">
              <Input
                value={user}
                aria-label={t.browse.onedriveUser}
                placeholder={t.browse.onedriveUserPlaceholder}
                onChange={(e) => setUser(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load({ user })}
              />
            </div>
            <Button size="sm" onClick={() => load({ user })}>
              {t.browse.openUser}
            </Button>
          </div>
          <p className="mt-1.5 text-[12px] leading-[1.45] text-fg-subtle">{t.browse.onedriveUserHint}</p>
        </div>
      )}
      {surface === 'drive' && Object.keys(at).length === 0 && (
        <p className="mb-2 text-[12px] leading-[1.45] text-fg-subtle">{t.browse.driveHint}</p>
      )}

      {trail.length > 0 && (
        <nav aria-label={t.browse.whereYouAre} className="mb-2 flex flex-wrap items-center gap-1 text-[12.5px]">
          {trail.map((crumb, index) => (
            <span key={`${JSON.stringify(crumb.at)}|${crumb.name}`} className="flex items-center gap-1">
              {index > 0 && <ChevronRight size={11} className="text-fg-subtle" aria-hidden />}
              {/* Crumb i goes back to the level BEFORE the i-th descent, so it wears that level's name. */}
              <button type="button" onClick={() => goBack(index)} className="rounded px-1 py-0.5 hover:bg-card-solid">
                {index === 0 ? SURFACE_LABEL[surface] : (trail[index - 1]?.name ?? crumb.name)}
              </button>
            </span>
          ))}
          <ChevronRight size={11} className="text-fg-subtle" aria-hidden />
          <span className="px-1 font-semibold">{trail[trail.length - 1]?.name ?? ''}</span>
        </nav>
      )}

      {browse.isPending && !page ? (
        <p className="flex items-center gap-2 text-[12.5px] text-fg-subtle">
          <Spinner size={11} /> {t.browse.loading}
        </p>
      ) : browse.isError ? (
        <p role="alert" className="flex items-start gap-1.5 text-[12.5px] leading-[1.45] text-alert">
          <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
          {describeApiError(fmt.t, browse.error)}
        </p>
      ) : page ? (
        <>
          {page.entries.length === 0 ? (
            <p className="text-[12.5px] text-fg-subtle">{t.browse.nothingHere}</p>
          ) : (
            <table className="w-full border-collapse text-[13px]">
              <thead className="sr-only">
                <tr>
                  <th>{t.browse.columns.name}</th>
                  <th>{t.browse.columns.contents}</th>
                  <th>{t.browse.columns.choose}</th>
                </tr>
              </thead>
              <tbody>
                {page.entries.map((entry) => (
                  <tr key={entry.remoteId} className="border-t border-rule first:border-t-0">
                    <td className="py-1.5 pr-2">
                      <button
                        type="button"
                        aria-label={t.browse.open(entry.name)}
                        onClick={() => descend(entry)}
                        className="flex items-center gap-1.5 text-left font-medium tracking-[-0.014em] underline decoration-transparent underline-offset-2 transition-colors duration-200 hover:decoration-current"
                      >
                        <ChevronRight size={12} className="shrink-0 text-fg-subtle" aria-hidden />
                        {entry.name}
                      </button>
                    </td>
                    <td className="readout whitespace-nowrap py-1.5 pr-2 text-right text-fg-subtle">
                      {entry.childCount !== null ? t.browse.items(fmt, entry.childCount) : ''}
                    </td>
                    <td className="py-1.5 text-right">
                      {entry.kind === 'folder' && page.root !== null && (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={t.browse.choose(entry.name)}
                          disabled={choosing}
                          onClick={() => {
                            // Choosing a child without descending: the root is that child's.
                            const rootFor = childRoot(page.root as ConnectorRoot, entry)
                            const path = [connection.accountLabel, ...trail.map((t2) => t2.name), entry.name].join(
                              ' / '
                            )
                            onChoose(rootFor, surface === 's3' ? '' : path)
                          }}
                        >
                          {t.browse.chooseAction}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {page.filesHere !== null && (
            <p className="readout mt-1.5 text-fg-subtle">{t.browse.filesHere(fmt, page.filesHere)}</p>
          )}
          {page.next && (
            <Button
              size="sm"
              variant="ghost"
              className="mt-1.5"
              onClick={() => load(at, { nextPage: page.next as string })}
            >
              {t.browse.more}
            </Button>
          )}
          {page.root && (
            <div className="mt-3 border-t border-rule pt-3">
              <Button variant="primary" size="sm" disabled={choosing} onClick={chooseHere}>
                {choosing ? t.browse.counting : t.browse.chooseThis}
              </Button>
            </div>
          )}
        </>
      ) : null}
    </Panel>
  )
}

const parseCrumbs = (raw: string | undefined): Array<{ remoteId: string; name: string }> => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** The root a child folder would be, from the level's own root. */
const childRoot = (levelRoot: ConnectorRoot, entry: BrowsePage['entries'][number]): ConnectorRoot => {
  if (levelRoot.surface === 'sharepoint' || levelRoot.surface === 'onedrive')
    return { ...levelRoot, itemId: entry.remoteId }
  if (levelRoot.surface === 'drive') return { ...levelRoot, fileId: entry.remoteId }
  if (levelRoot.surface === 's3') return { ...levelRoot, prefix: entry.remoteId === '/' ? '' : entry.remoteId }
  return levelRoot
}

export type { ConnectorSurface as ConnectSurface }
export { SURFACE_LABEL as connectorSurfaceLabel }
